import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Chip, 
  Button, 
  Avatar, 
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Alert,
  Paper,
  Stack,
  useTheme,
  alpha,
  Slide,
  IconButton,
  Tooltip
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import apiClient from '../../request/http-common';
import { setInvestments } from '../../request/Analysis/hitorySlice';
import { useDispatch } from 'react-redux';
import { Currency } from '@depay/local-currency';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TimerIcon from '@mui/icons-material/Timer';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import PieChartIcon from '@mui/icons-material/PieChart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import StarIcon from '@mui/icons-material/Star';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const InvestmentPlanDetail = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.user);
  const plans = useSelector((state) => state.plans.plans);
  
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(0);
  const [duration, setDuration] = useState('');
  const [source, setSource] = useState('');
  const [rate, setRate] = useState(1);
  const [error, setError] = useState('');

  const fundingBal = useSelector((state) => state.wallet.fundingBalance);
  const subBal = useSelector((state) => state.wallet.subscriptionBalance);
  const refBal = useSelector((state) => state.wallet.referralBalance);
  const mainBal = useSelector((state) => state.wallet.withdrawableBalance);

  useEffect(() => {
    if (plans && plans.length > 0) {
      const foundPlan = plans.find(p => p.id == id);
      setPlan(foundPlan);
    }
  }, [plans, id]);

  useEffect(() => {
    if (user.currency === 'USD') {
      setRate(1);
    } else {
      Currency.fromUSD({ amount: 1 }).then(amount => {
        setRate(amount.amount);
      });
    }
  }, [user.currency]);

  const formatPrice = (amount) => new Intl.NumberFormat('en-US', { 
    style: 'currency', 
    currency: 'USD', 
    currencySign: 'standard' 
  }).format(amount);

  const { isLoading: isInvesting, mutate: postInvestment } = useMutation(
    async () => {
      return await apiClient.post(`/api/invest`, {
        investment_plan_id: plan.id,
        amount: amount, 
        rate: rate,
        duration: duration,
        payment_source: source
      });
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: `${res.data.message}`, 
            type: 'success' 
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          dispatch(setInvestments(res.data.investments));
          setTimeout(() => {
            navigate('/investments');
          }, 2000);
        }            
      },
      onError: (err) => {
        if (err.response?.data.length) {
          let myerror = err.response?.data || err;         
          setError(myerror.errors)               
        } else {
          let errorMessage = err.response?.data.message || err;
          localStorage.setItem('notification', JSON.stringify({
            message: `${errorMessage}`, 
            type: 'error' 
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
        }
      },
    }
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (!plan) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading investment plan...</Typography>
      </Box>
    );
  }

  const holdings = plan.holdings_allocation ? 
    (typeof plan.holdings_allocation === 'string' ? 
      (() => {
        try {
          // Handle double-encoded JSON (escaped JSON within JSON string)
          let parsed = JSON.parse(plan.holdings_allocation);
          
          // If the parsed result is still a string, parse it again
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
          }
          
          return parsed;
        } catch (e) {
          console.error('Error parsing holdings_allocation:', e);
          console.log('Raw holdings_allocation:', plan.holdings_allocation);
          return {};
        }
      })() : 
      plan.holdings_allocation) : {};

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: isDarkMode 
        ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      py: 3
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/package/Invest Funds')}
            sx={{ 
              mb: 3,
              borderRadius: 2,
              textTransform: 'none',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            Back to Investment Vehicle
          </Button>
          
          <Box sx={{ 
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: 3,
            p: 4,
            boxShadow: isDarkMode
              ? '0 20px 60px rgba(0,0,0,0.3)'
              : '0 20px 60px rgba(0,0,0,0.1)',
            border: isDarkMode 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.08)'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h3" sx={{ 
                  fontWeight: 700, 
                  mb: 1,
                  color: theme.palette.text.primary,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  {plan.name}
                </Typography>
                
                {plan.investment_type && (
                  <Chip 
                    label={plan.investment_type}
                    sx={{ 
                      bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.2) : '#e6fffa',
                      color: isDarkMode ? theme.palette.primary.light : '#065f46',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      height: 32
                    }}
                  />
                )}
              </Box>
              
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: theme.palette.success.main,
                  mb: 0.5
                }}>
                  {plan.min_return_percentage && plan.max_return_percentage 
                    ? `${plan.min_return_percentage}% - ${plan.max_return_percentage}%` 
                    : `${parseFloat(plan.total_earning).toFixed(2)}%`}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Expected Returns
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        <Grid container spacing={4}>
          {/* Left Column - Plan Details */}
          <Grid item xs={12} lg={8}>
            {/* Description */}
            {plan.description && (
              <Card sx={{ 
                mb: 3,
                borderRadius: 3,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: isDarkMode 
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(0,0,0,0.08)',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(0,0,0,0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <InfoIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      Fund Description
                    </Typography>
                  </Stack>
                  <Typography variant="body1" sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.7,
                    fontSize: '1.1rem'
                  }}>
                    {plan.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Key Metrics */}
            <Card sx={{ 
              mb: 3,
              borderRadius: 3,
              background: isDarkMode 
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: isDarkMode 
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.08)',
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0,0,0,0.3)'
                : '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    Investment Details
                  </Typography>
                </Stack>
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 2,
                      bgcolor: isDarkMode ? alpha(theme.palette.success.main, 0.1) : '#f0fdf4',
                      border: isDarkMode ? `1px solid ${alpha(theme.palette.success.main, 0.2)}` : '1px solid #bbf7d0'
                    }}>
                      <TrendingUpIcon sx={{ color: theme.palette.success.main, fontSize: 32, mb: 1 }} />
                      <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 0.5 }}>
                        {plan.min_return_percentage && plan.max_return_percentage 
                          ? `${plan.min_return_percentage}% - ${plan.max_return_percentage}%` 
                          : `${parseFloat(plan.total_earning).toFixed(2)}%`}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Expected Return
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 2,
                      bgcolor: isDarkMode ? alpha(theme.palette.info.main, 0.1) : '#eff6ff',
                      border: isDarkMode ? `1px solid ${alpha(theme.palette.info.main, 0.2)}` : '1px solid #bfdbfe'
                    }}>
                      <ScheduleIcon sx={{ color: theme.palette.info.main, fontSize: 32, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.info.main, mb: 0.5 }}>
                        {plan.investment_duration_range || plan.duration}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Duration
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 2,
                      bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : '#f0f9ff',
                      border: isDarkMode ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}` : '1px solid #bae6fd'
                    }}>
                      <MonetizationOnIcon sx={{ color: theme.palette.primary.main, fontSize: 32, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
                        {formatPrice(plan.min_amount)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Min. Investment
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Paper elevation={0} sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      borderRadius: 2,
                      bgcolor: isDarkMode ? alpha(theme.palette.warning.main, 0.1) : '#fffbeb',
                      border: isDarkMode ? `1px solid ${alpha(theme.palette.warning.main, 0.2)}` : '1px solid #fed7aa'
                    }}>
                      <SecurityIcon sx={{ color: theme.palette.warning.main, fontSize: 32, mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.warning.main, mb: 0.5 }}>
                        {formatPrice(plan.min_additional_investment || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                        Min. Add
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Holdings Allocation */}
            {Object.keys(holdings).length > 0 && (
              <Card sx={{ 
                mb: 3,
                borderRadius: 3,
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                  : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: isDarkMode 
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid rgba(0,0,0,0.08)',
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(0,0,0,0.3)'
                  : '0 8px 32px rgba(0,0,0,0.1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <PieChartIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      Top Holdings / Allocation
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {Object.entries(holdings).map(([holding, percentage], index) => {
                      // Extract numeric value from percentage string (e.g., "31.1%" -> 31.1)
                      const numericValue = parseFloat(percentage?.replace('%', '') || '0');
                      
                      return (
                        <Grid item xs={12} sm={6} key={index}>
                          <Paper elevation={0} sx={{ 
                            p: 3, 
                            borderRadius: 2,
                            bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : '#f8fafc',
                            border: isDarkMode ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : '1px solid #e2e8f0',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: isDarkMode 
                                ? '0 8px 25px rgba(0,0,0,0.3)' 
                                : '0 8px 25px rgba(0,0,0,0.1)'
                            }
                          }}>
                            <Stack spacing={2}>
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Box sx={{ 
                                  p: 1.5, 
                                  borderRadius: 1.5, 
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <PieChartIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle1" sx={{ 
                                    fontWeight: 600, 
                                    color: theme.palette.text.primary,
                                    mb: 0.5
                                  }}>
                                    {holding || 'Unknown Holding'}
                                  </Typography>
                                  <Typography variant="h6" sx={{ 
                                    fontWeight: 700, 
                                    color: theme.palette.primary.main 
                                  }}>
                                    {percentage || 'N/A'}
                                  </Typography>
                                </Box>
                              </Stack>
                              
                              {/* Progress Bar */}
                              <Box sx={{ width: '100%' }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={numericValue}
                                  sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                      borderRadius: 4,
                                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                    }
                                  }}
                                />
                              </Box>
                            </Stack>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* Debug Holdings Data - Remove this in production */}
            {process.env.NODE_ENV === 'development' && (
              <Card sx={{ mb: 3, p: 2, bgcolor: 'warning.light' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Debug: Holdings Data</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Raw holdings_allocation:</strong> {JSON.stringify(plan.holdings_allocation)}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Parsed holdings:</strong> {JSON.stringify(holdings)}
                </Typography>
                <Typography variant="body2">
                  <strong>Holdings keys count:</strong> {Object.keys(holdings).length}
                </Typography>
              </Card>
            )}
          </Grid>

          {/* Right Column - Investment Form */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ 
              position: 'sticky', 
              top: 20,
              borderRadius: 3,
              background: isDarkMode 
                ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: isDarkMode 
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.08)',
              boxShadow: isDarkMode
                ? '0 8px 32px rgba(0,0,0,0.3)'
                : '0 8px 32px rgba(0,0,0,0.1)'
            }}>
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BusinessCenterIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                    Invest in this Fund
                  </Typography>
                </Stack>
                
                <Button 
                  variant="contained" 
                  fullWidth 
                  size="large"
                  startIcon={<BusinessCenterIcon />}
                  onClick={handleClickOpen}
                  sx={{ 
                    mb: 3,
                    py: 2,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker || theme.palette.primary.dark} 100%)`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    }
                  }}
                >
                  Start Investment
                </Button>

                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    bgcolor: isDarkMode ? alpha(theme.palette.info.main, 0.1) : undefined,
                    border: isDarkMode ? `1px solid ${alpha(theme.palette.info.main, 0.2)}` : undefined
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <strong>Investment Range:</strong> {formatPrice(plan.min_amount)} - {formatPrice(plan.amount_invested)}
                  </Typography>
                  {plan.min_additional_investment && (
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Min. Additional:</strong> {formatPrice(plan.min_additional_investment)}
                    </Typography>
                  )}
                </Alert>

                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6
                }}>
                  This fund offers a balanced approach to growth and capital preservation through diversified holdings.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Investment Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        sx={{
          zIndex: 1300, // Standard Material-UI modal z-index
          '& .MuiBackdrop-root': {
            zIndex: 1299,
          }
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            boxShadow: isDarkMode
              ? '0 20px 60px rgba(0,0,0,0.4)'
              : '0 20px 60px rgba(0,0,0,0.15)',
            zIndex: 1300,
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 0,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          borderRadius: '12px 12px 0 0'
        }}>
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Invest in {plan.name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Complete your investment setup
              </Typography>
            </Box>
            <IconButton 
              onClick={handleClose}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {/* Investment Summary */}
          <Paper elevation={0} sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 2,
            background: isDarkMode 
              ? alpha(theme.palette.primary.main, 0.1)
              : 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
            border: isDarkMode 
              ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
              : '1px solid #bbf7d0'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.text.primary }}>
              Investment Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: theme.palette.background.paper, borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Expected Returns
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    {plan.min_return_percentage && plan.max_return_percentage
                      ? `${plan.min_return_percentage}% - ${plan.max_return_percentage}%`
                      : `${parseFloat(plan.total_earning).toFixed(2)}%`}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: theme.palette.background.paper, borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                    Duration
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                    {plan.investment_duration_range || plan.duration}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6" sx={{ textAlign: 'center', mb: 3, color: theme.palette.text.primary, fontWeight: 600 }}>
            Investment Configuration
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center', mb: 4, color: theme.palette.text.secondary }}>
            Enter any amount from {formatPrice(plan.min_amount)} to {formatPrice(plan.amount_invested)} to invest
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Investment Amount */}
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Investment Amount</InputLabel>
              <OutlinedInput
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                label="Investment Amount"
                type="number"
                startAdornment={
                  <Box sx={{ mr: 1, color: theme.palette.text.secondary }}>
                    <MonetizationOnIcon />
                  </Box>
                }
                sx={{
                  borderRadius: 2,
                  zIndex: 1, // Ensure input is above backdrop
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                }}
              />
            </FormControl>

            {/* Duration Selection */}
            {plan.duration && plan.duration.split(',').length > 0 ? (
              <FormControl fullWidth>
                <InputLabel sx={{ color: theme.palette.text.secondary }}>Select Duration</InputLabel>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  label="Select Duration"
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        zIndex: 1400, // Higher than modal to appear above
                      }
                    }
                  }}
                  startAdornment={
                    <Box sx={{ mr: 1, color: theme.palette.text.secondary }}>
                      <ScheduleIcon />
                    </Box>
                  }
                  sx={{
                    borderRadius: 2,
                    zIndex: 1, // Ensure select is above backdrop
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                      borderWidth: 2,
                    },
                  }}
                >
                  <MenuItem value="">Select duration</MenuItem>
                  {plan.duration.split(',').map(durationOption => (
                    <MenuItem key={durationOption.trim()} value={durationOption.trim()}>
                      {durationOption.trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            {/* Payment Source */}
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Select Payment Source</InputLabel>
              <Select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                label="Select Payment Source"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 1400, // Higher than modal to appear above
                    }
                  }
                }}
                startAdornment={
                  <Box sx={{ mr: 1, color: theme.palette.text.secondary }}>
                    <AccountBalanceWalletIcon />
                  </Box>
                }
                sx={{
                  borderRadius: 2,
                  zIndex: 1, // Ensure select is above backdrop
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                }}
              >
                <MenuItem value="">Select Payment Source</MenuItem>
                <MenuItem value="Withdrawable Balance">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <AccountBalanceWalletIcon />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Spot Wallet
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Balance: {formatPrice(mainBal)}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
                <MenuItem value="Referral Wallet">
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <AccountBalanceWalletIcon />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Referral Wallet
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Balance: {formatPrice(refBal)}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Investment Preview */}
            {amount && (
              <Paper elevation={0} sx={{ 
                p: 3, 
                borderRadius: 2,
                background: isDarkMode ? alpha(theme.palette.success.main, 0.1) : '#f0fdf4',
                border: isDarkMode ? `1px solid ${alpha(theme.palette.success.main, 0.2)}` : '1px solid #bbf7d0'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: theme.palette.success.main }}>
                  Investment Preview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Investment Amount
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                      {formatPrice(amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Expected Return
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                      {plan.min_return_percentage && plan.max_return_percentage
                        ? `${formatPrice(amount * (plan.min_return_percentage / 100))} - ${formatPrice(amount * (plan.max_return_percentage / 100))}`
                        : `${formatPrice(amount * (parseFloat(plan.total_earning) / 100))}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 0 }}>
          <Button 
            onClick={handleClose}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={postInvestment} 
            variant="contained"
            disabled={isInvesting || !amount || !source}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.darker || theme.palette.primary.dark} 100%)`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              }
            }}
          >
            {isInvesting ? 'Processing Investment...' : 'Complete Investment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvestmentPlanDetail;
